import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { studioHeaders } from "@/lib/studioAuth";
import { Loader2, Plus, Trash2, RotateCcw, Save, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * KitContentTab — lets Kaily edit the marketing copy on the /offer page.
 *
 * Notes:
 * - PRICING for Kit tiers (kit-starter, kit-pro, kit-premium) is edited in the
 *   "Packages" tab. This tab is purely for marketing COPY.
 * - Every field is optional — if left blank, the hardcoded fallback in
 *   Offer.jsx wins. So a brand new Kaily can start by tweaking only ONE field
 *   (e.g. version badge) without touching anything else.
 */

const emptyInside = () => ({ n: "", t_en: "", d_en: "", t_cn: "", d_cn: "" });
const emptyFaq = () => ({ q_en: "", a_en: "", q_cn: "", a_cn: "" });
const emptyStat = () => ({ n: "", l_en: "", l_cn: "" });

export default function KitContentTab() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [c, setC] = useState({
        hero_badge_en: "",
        hero_badge_cn: "",
        hero_sub_en: "",
        hero_sub_cn: "",
        problem_title_en: "",
        problem_title_cn: "",
        problem_body_en: "",
        problem_body_cn: "",
        founder_body_en: "",
        founder_body_cn: "",
        inside_title_en: "",
        inside_title_cn: "",
        inside_items: [],
        guarantee_en: "",
        guarantee_cn: "",
        faq_items: [],
        stats: [],
        final_title_en: "",
        final_title_cn: "",
        final_sub_en: "",
        final_sub_cn: "",
        final_cta_kit_en: "",
        final_cta_kit_cn: "",
    });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/kit-content`, { params: { _t: Date.now() } });
            setC((prev) => ({ ...prev, ...(data || {}) }));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load content");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const upd = (key) => (v) => setC((p) => ({ ...p, [key]: typeof v === "string" ? v : v?.target?.value ?? v }));

    const updArr = (key, idx, sub) => (v) => setC((p) => {
        const arr = [...(p[key] || [])];
        arr[idx] = { ...arr[idx], [sub]: typeof v === "string" ? v : v?.target?.value ?? v };
        return { ...p, [key]: arr };
    });

    const addItem = (key, factory) => () => setC((p) => ({ ...p, [key]: [...(p[key] || []), factory()] }));
    const removeItem = (key, idx) => () => setC((p) => ({ ...p, [key]: (p[key] || []).filter((_, i) => i !== idx) }));

    const save = async () => {
        setSaving(true);
        try {
            // Strip empty strings to let the hardcoded fallback win on blank fields.
            const payload = {};
            Object.entries(c).forEach(([k, v]) => {
                if (Array.isArray(v)) {
                    const cleaned = v
                        .map((item) => {
                            const o = {};
                            Object.entries(item).forEach(([kk, vv]) => {
                                if (vv && String(vv).trim()) o[kk] = String(vv).trim();
                            });
                            return o;
                        })
                        .filter((o) => Object.keys(o).length > 0);
                    if (cleaned.length > 0) payload[k] = cleaned;
                } else if (v && String(v).trim()) {
                    payload[k] = String(v).trim();
                }
            });
            await axios.put(`${API}/admin/kit-content`, payload, { headers: studioHeaders() });
            toast.success("Kit content saved · live on /offer in a few seconds");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const resetAll = async () => {
        if (!window.confirm("Reset ALL kit-content overrides? This restores the hardcoded defaults. Cannot be undone.")) return;
        try {
            await axios.post(`${API}/admin/kit-content/reset`, {}, { headers: studioHeaders() });
            toast.success("Reset to defaults");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Reset failed");
        }
    };

    if (loading) {
        return <div className="text-cafe-mocha py-12 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin mb-3" />Loading…</div>;
    }

    return (
        <div data-testid="kit-content-tab" className="max-w-4xl">
            {/* Header */}
            <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
                <div>
                    <h2 className="font-display text-2xl text-cafe-espresso leading-tight">Kit content</h2>
                    <p className="text-cafe-mocha text-sm">
                        Edit the marketing copy on <a href="/ig/content/AI" target="_blank" rel="noopener noreferrer" className="text-cafe-olive hover:underline">/ig/content/AI <ExternalLink className="inline h-3 w-3" /></a>. Leave a field blank to use the hardcoded default. Pricing is in the <em>Packages</em> tab.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={resetAll} className="rounded-full bg-transparent text-cafe-mocha text-xs h-9" data-testid="kit-content-reset">
                        <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset all
                    </Button>
                    <Button onClick={save} disabled={saving} className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-9 px-5" data-testid="kit-content-save">
                        {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                        Save all
                    </Button>
                </div>
            </div>

            {/* Hero section */}
            <Section title="Hero badge · the version label visible top-left of /offer">
                <Row>
                    <Field label="Badge · EN" placeholder="AI CONTENT GROWTH KIT · V2.4" value={c.hero_badge_en} onChange={upd("hero_badge_en")} testid="hero-badge-en" />
                    <Field label="Badge · CN" placeholder="AI 内容增长配套 · V2.4" value={c.hero_badge_cn} onChange={upd("hero_badge_cn")} testid="hero-badge-cn" />
                </Row>
                <Row>
                    <TextArea label="Sub-headline · EN" placeholder="Captions, hooks, AI visuals…" value={c.hero_sub_en} onChange={upd("hero_sub_en")} testid="hero-sub-en" rows={3} />
                    <TextArea label="Sub-headline · CN" placeholder="文案、钩子、AI 视觉…" value={c.hero_sub_cn} onChange={upd("hero_sub_cn")} testid="hero-sub-cn" rows={3} />
                </Row>
            </Section>

            {/* Stats */}
            <Section title="Stats row · 4 numbers under the hero">
                {(c.stats || []).map((s, i) => (
                    <div key={i} className="grid grid-cols-1 md:grid-cols-[120px_1fr_1fr_40px] gap-3 items-end mb-3 pb-3 border-b border-cafe-espresso/5 last:border-0">
                        <Field label="Number" placeholder="50+" value={s.n} onChange={updArr("stats", i, "n")} testid={`stat-n-${i}`} />
                        <Field label="Label · EN" placeholder="cafes served" value={s.l_en} onChange={updArr("stats", i, "l_en")} testid={`stat-len-${i}`} />
                        <Field label="Label · CN" placeholder="服务过的餐厅" value={s.l_cn} onChange={updArr("stats", i, "l_cn")} testid={`stat-lcn-${i}`} />
                        <Button variant="ghost" size="icon" onClick={removeItem("stats", i)} className="text-red-500 mt-5"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                ))}
                <Button variant="outline" onClick={addItem("stats", emptyStat)} className="rounded-full bg-transparent text-xs h-8 mt-1"><Plus className="h-3 w-3 mr-1" /> Add stat</Button>
            </Section>

            {/* Problem */}
            <Section title="Problem block · 'The 1 AM struggle'">
                <Row>
                    <Field label="Title · EN" placeholder="Owning a cafe is 80 hours…" value={c.problem_title_en} onChange={upd("problem_title_en")} testid="problem-title-en" />
                    <Field label="Title · CN" placeholder="开咖啡馆已经一周 80 小时…" value={c.problem_title_cn} onChange={upd("problem_title_cn")} testid="problem-title-cn" />
                </Row>
                <Row>
                    <TextArea label="Body · EN" value={c.problem_body_en} onChange={upd("problem_body_en")} testid="problem-body-en" rows={3} />
                    <TextArea label="Body · CN" value={c.problem_body_cn} onChange={upd("problem_body_cn")} testid="problem-body-cn" rows={3} />
                </Row>
            </Section>

            {/* Founder */}
            <Section title="Founder block · 'Meet Kaily'">
                <Row>
                    <TextArea label="Bio · EN" value={c.founder_body_en} onChange={upd("founder_body_en")} testid="founder-body-en" rows={4} />
                    <TextArea label="Bio · CN" value={c.founder_body_cn} onChange={upd("founder_body_cn")} testid="founder-body-cn" rows={4} />
                </Row>
            </Section>

            {/* Inside the kit */}
            <Section title="Inside the kit · ordered cards (drag-reorder coming soon)">
                <Row>
                    <Field label="Section title · EN" placeholder="Not a stock pack. A 30-day publishing system." value={c.inside_title_en} onChange={upd("inside_title_en")} testid="inside-title-en" />
                    <Field label="Section title · CN" placeholder="不是图片包,是 30 天发布系统。" value={c.inside_title_cn} onChange={upd("inside_title_cn")} testid="inside-title-cn" />
                </Row>
                {(c.inside_items || []).map((item, i) => (
                    <div key={i} className="bg-white/60 border border-cafe-espresso/8 rounded-xl p-4 mb-3">
                        <div className="grid grid-cols-1 md:grid-cols-[80px_1fr_40px] gap-3 items-start">
                            <Field label="N°" placeholder="01" value={item.n} onChange={updArr("inside_items", i, "n")} testid={`inside-n-${i}`} />
                            <div className="space-y-3">
                                <Row>
                                    <Field label="Title · EN" placeholder="30 Captions" value={item.t_en} onChange={updArr("inside_items", i, "t_en")} testid={`inside-ten-${i}`} />
                                    <Field label="Title · CN" placeholder="30 条文案" value={item.t_cn} onChange={updArr("inside_items", i, "t_cn")} testid={`inside-tcn-${i}`} />
                                </Row>
                                <Row>
                                    <TextArea label="Description · EN" value={item.d_en} onChange={updArr("inside_items", i, "d_en")} testid={`inside-den-${i}`} rows={2} />
                                    <TextArea label="Description · CN" value={item.d_cn} onChange={updArr("inside_items", i, "d_cn")} testid={`inside-dcn-${i}`} rows={2} />
                                </Row>
                            </div>
                            <Button variant="ghost" size="icon" onClick={removeItem("inside_items", i)} className="text-red-500 mt-5"><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                    </div>
                ))}
                <Button variant="outline" onClick={addItem("inside_items", emptyInside)} className="rounded-full bg-transparent text-xs h-8"><Plus className="h-3 w-3 mr-1" /> Add inside-the-kit item</Button>
            </Section>

            {/* Guarantee */}
            <Section title="Guarantee · the thin strip with refund text">
                <Row>
                    <TextArea label="Text · EN" placeholder="14-day refund on every Kit · Cancel Continuity anytime" value={c.guarantee_en} onChange={upd("guarantee_en")} testid="guarantee-en" rows={2} />
                    <TextArea label="Text · CN" placeholder="每份配套 14 天退款 · Continuity 可随时取消" value={c.guarantee_cn} onChange={upd("guarantee_cn")} testid="guarantee-cn" rows={2} />
                </Row>
            </Section>

            {/* FAQ */}
            <Section title="FAQ · displayed in an accordion">
                {(c.faq_items || []).map((f, i) => (
                    <div key={i} className="bg-white/60 border border-cafe-espresso/8 rounded-xl p-4 mb-3">
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_40px] gap-3 items-start">
                            <div className="space-y-3">
                                <Row>
                                    <Field label="Question · EN" placeholder="Are the AI images on-brand?" value={f.q_en} onChange={updArr("faq_items", i, "q_en")} testid={`faq-qen-${i}`} />
                                    <Field label="Question · CN" value={f.q_cn} onChange={updArr("faq_items", i, "q_cn")} testid={`faq-qcn-${i}`} />
                                </Row>
                                <Row>
                                    <TextArea label="Answer · EN" value={f.a_en} onChange={updArr("faq_items", i, "a_en")} testid={`faq-aen-${i}`} rows={3} />
                                    <TextArea label="Answer · CN" value={f.a_cn} onChange={updArr("faq_items", i, "a_cn")} testid={`faq-acn-${i}`} rows={3} />
                                </Row>
                            </div>
                            <Button variant="ghost" size="icon" onClick={removeItem("faq_items", i)} className="text-red-500 mt-5"><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                    </div>
                ))}
                <Button variant="outline" onClick={addItem("faq_items", emptyFaq)} className="rounded-full bg-transparent text-xs h-8"><Plus className="h-3 w-3 mr-1" /> Add FAQ item</Button>
            </Section>

            {/* Final CTA */}
            <Section title="Final CTA · 'Ready to slow down?'">
                <Row>
                    <Field label="Title · EN" value={c.final_title_en} onChange={upd("final_title_en")} testid="final-title-en" />
                    <Field label="Title · CN" value={c.final_title_cn} onChange={upd("final_title_cn")} testid="final-title-cn" />
                </Row>
                <Row>
                    <TextArea label="Sub · EN" value={c.final_sub_en} onChange={upd("final_sub_en")} testid="final-sub-en" rows={2} />
                    <TextArea label="Sub · CN" value={c.final_sub_cn} onChange={upd("final_sub_cn")} testid="final-sub-cn" rows={2} />
                </Row>
                <Row>
                    <Field label="CTA button label · EN" placeholder="Get the Growth Kit" value={c.final_cta_kit_en} onChange={upd("final_cta_kit_en")} testid="final-cta-en" />
                    <Field label="CTA button label · CN" placeholder="获取增长配套" value={c.final_cta_kit_cn} onChange={upd("final_cta_kit_cn")} testid="final-cta-cn" />
                </Row>
            </Section>

            {/* Sticky save bar — repeated for long pages */}
            <div className="sticky bottom-4 z-10 bg-cafe-cream/95 backdrop-blur border border-cafe-espresso/15 rounded-2xl px-5 py-3 flex items-center justify-between shadow-lg mt-8">
                <span className="text-xs text-cafe-mocha">Don't forget to click <strong>Save all</strong> · empty fields use hardcoded defaults</span>
                <Button onClick={save} disabled={saving} className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-9 px-5 text-xs">
                    {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                    Save all changes
                </Button>
            </div>
        </div>
    );
}


function Section({ title, children }) {
    return (
        <div className="bg-cafe-cream border border-cafe-espresso/10 rounded-2xl p-5 md:p-6 mb-5">
            <h3 className="font-display text-lg text-cafe-espresso mb-4">{title}</h3>
            {children}
        </div>
    );
}

function Row({ children }) {
    return <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">{children}</div>;
}

function Field({ label, value, onChange, placeholder, testid }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-cafe-mocha font-semibold">{label}</Label>
            <Input
                value={value || ""}
                onChange={onChange}
                placeholder={placeholder}
                data-testid={testid}
                className="mt-1 bg-white border-cafe-espresso/10 rounded-lg text-sm h-9"
            />
        </div>
    );
}

function TextArea({ label, value, onChange, placeholder, rows = 3, testid }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-cafe-mocha font-semibold">{label}</Label>
            <Textarea
                value={value || ""}
                onChange={onChange}
                placeholder={placeholder}
                rows={rows}
                data-testid={testid}
                className="mt-1 bg-white border-cafe-espresso/10 rounded-lg text-sm"
            />
        </div>
    );
}
